import { SyncRecord } from '../../../components/CoSell/types'
import { ReferralMetadata } from '../reducer'
import { LOCATION_DEFAULT } from '../../../../externalApps/HubspotApp/ReferralForm/AzureReferralForm/constants'
import type {
  AzureCustomerPurchaseIntent,
  AzureFormStatus,
  AzureMpcReferralType,
  AzureSolutionType,
  DealType,
  ReferralSalesStage,
  ReferralStatus,
  ReferralSubstatus,
} from './azureReferralSchema'
import {
  AZURE_CUSTOMER_PURCHASE_INTENT,
  AZURE_DEAL_TYPE,
  AZURE_DEAL_TYPE_OPTIONS,
  AZURE_FORM_STATUS,
  AZURE_MPC_REFERRAL_TYPE,
  AZURE_REFERRAL_CREATE_STATUS,
  AZURE_REFERRAL_CREATE_SUBSTATUS,
  AZURE_REFERRAL_NEW_SALES_STAGE,
  AZURE_REFERRAL_SALES_STAGE,
  AZURE_REFERRAL_STATUS,
  AZURE_REFERRAL_SUBSTATUS,
  AZURE_SOLUTION_TYPE,
  SHARED_AZURE_DEAL_TYPES,
  isAzureDealType,
  isAzureFormStatus,
  isAzureReferralStatus,
  isAzureReferralSubstatus,
  isAzureSolutionType,
} from './azureReferralSchema'

export {
  AZURE_CUSTOMER_PURCHASE_INTENT,
  AZURE_DEAL_TYPE,
  AZURE_DEAL_TYPE_OPTIONS,
  AZURE_FORM_STATUS,
  AZURE_MPC_REFERRAL_TYPE,
  AZURE_REFERRAL_CREATE_STATUS,
  AZURE_REFERRAL_CREATE_SUBSTATUS,
  AZURE_REFERRAL_NEW_SALES_STAGE,
  AZURE_REFERRAL_SALES_STAGE,
  AZURE_REFERRAL_STATUS,
  AZURE_REFERRAL_SUBSTATUS,
  AZURE_SOLUTION_TYPE,
  SHARED_AZURE_DEAL_TYPES,
  isAzureDealType,
  isAzureFormStatus,
  isAzureReferralStatus,
  isAzureReferralSubstatus,
  isAzureSolutionType,
}

export type {
  AzureCustomerPurchaseIntent,
  AzureFormStatus,
  AzureMpcReferralType,
  AzureSolutionType,
  DealType,
  ReferralSalesStage,
  ReferralStatus,
  ReferralSubstatus,
}

//TBD - update this to match the actual API object
export interface ReferralAzureFormAPI {
  Labra__Id__c: string
  Labra__Customer_Name__c: string
  Labra__Website__c: string
  Labra__City__c?: string
  Labra__State__c?: string
  Labra__Country__c: string
  Labra__Postal_Code__c?: string
  Labra__First_Name__c?: string
  Labra__Last_Name__c?: string
  Labra__Full_Name__c?: string
  Labra__Email__c?: string
  Labra__Phone__c?: string
  Labra__Address__c?: string
  Labra__Address_Line_2__c?: string
  Labra__Deal_Type__c: string
  Labra__Deal_Name__c: string
  Labra__Location__c: string
  Labra__Currency__c: string
  Labra__Estimated_Value__c: number
  Labra__Estimated_Close_Date__c: string
  Labra__CRM_Id__c: string
  Labra__Marketing_Campaign_ID__c: string
  Labra__Solution_Area__c: string
  Labra__Solution_Play__c: string
  Labra__Customer_Purchase_Intent__c: string
  Labra__Team_Members__c: string[]
  Labra__Partner_Notes__c: string
  Labra__Solutions_Offered__c: string[]
  Labra__Type_of_Help__c: string
  Labra__Would_Like_Microsoft_Sellers_To_View_Deal__c: boolean
  Labra__Notes_to_Microsoft__c: string
}

export interface ReferralAzureForm {
  id: string
  customerName: string
  website: string
  city?: string
  state?: string
  country: string
  postalCode?: string
  firstName?: string
  lastName?: string
  fullName?: string
  email?: string
  phone?: string
  addressLine1?: string
  addressLine2?: string
  dealType: string
  dealName: string
  location: string
  currency: string
  estimatedValue: number
  estimatedCloseDate: string
  crmId: string
  marketingCampaignID: string
  solutionArea: string
  solutionPlay: string
  customerPurchaseIntent: string
  teamMembers: string[]
  partnerNotes: string
  solutionsOffered: string[]
  typeOfHelp: string
  wouldLikeMicrosoftSellersToViewDeal: boolean
  notesToMicrosoft: string
}

export enum ReferralLeadFormSectionName {
  CUSTOMER_DETAILS = 'customerDetails',
  DEAL_DETAILS = 'dealDetails',
  SOLUTIONS_OFFERED = 'solutionsOffered',
  HELP_FROM_MICROSOFT = 'helpFromMicrosoft',
}

/** @deprecated Use ReferralLeadFormSectionName */
export const AzureReferralFormSectionName = ReferralLeadFormSectionName

export interface CustomerDetails {
  customerName?: string
  website?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  firstName?: string
  lastName?: string
  fullName?: string
  email?: string
  phone?: string | number
  addressLine1?: string
  addressLine2?: string
}

export interface ContactDetails {
  id: string
  firstName: string
  lastName: string
  phone: string
  email: string
}

export interface DealDetails {
  dealType?: DealType
  partnerRole?: string
  dealName?: string
  location?: string
  currency?: string
  estimatedValue?: number
  estimatedCloseDate?: string
  crmId?: string | number
  marketingCampaignID?: string
  solutionPlay?: string
  solutionArea?: string
  customerPurchaseIntent?: AzureCustomerPurchaseIntent
  teamMembers?: string[]
  partnerNotes?: string
  contactDetails?: ContactDetails[]
  salesStage?: string
  status?: string
  substatus?: string
  closedReason?: string
}

export interface BaseSolution {
  solutionId: string
  uuid?: string
  solutionName: string
  publisherName: string
  incentiveProgram?: string
}

export interface CompanySolution extends BaseSolution {
  solutionType: typeof AZURE_SOLUTION_TYPE.COMPANY_SOLUTION
}

export interface MicrosoftSolution extends BaseSolution {
  solutionType: typeof AZURE_SOLUTION_TYPE.MICROSOFT_SOLUTION
  quantity: number
  price: number
}

export interface ThirdPartySolution extends BaseSolution {
  solutionType: typeof AZURE_SOLUTION_TYPE.OTHER_THIRD_PARTY_SOLUTION
}

export type Solution = CompanySolution | MicrosoftSolution

export type ApiSolutionFormat = {
  id: string
  name: string
  solutionType: string
  publisherName: string
  type?: string
  price?: number
  currency?: string
  quantity?: number
}

export interface SolutionsOffered {
  companySolutions: CompanySolution[]
  microsoftSolutions: MicrosoftSolution[]
  selectedSolutions: string[]
  /** Legacy GET/API format — solution IDs only */
  solutionsOffered?: string[]
  /** Raw API solution objects from GET, used before UI hydration */
  apiSolutions?: ApiSolutionFormat[]
}

export type AzureTypeOfHelp =
  | 'No help required at this time'
  | 'Proof of Concept/Demo'

export interface HelpFromMicrosoft {
  typeOfHelp?: AzureTypeOfHelp | ''
  sellerDealView?: 'YES' | 'NO'
  notesToMicrosoft?: string
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
}

export type ReferralAzureFormSections = {
  [ReferralLeadFormSectionName.CUSTOMER_DETAILS]: CustomerDetails
  [ReferralLeadFormSectionName.DEAL_DETAILS]: DealDetails
  [ReferralLeadFormSectionName.SOLUTIONS_OFFERED]: SolutionsOffered
  [ReferralLeadFormSectionName.HELP_FROM_MICROSOFT]: HelpFromMicrosoft
}

/** Canonical alias used by Formik and HubSpot UI components */
export type AzureReferralOppSections = ReferralAzureFormSections

export type AzureReferralOppSectionFields =
  | keyof CustomerDetails
  | keyof DealDetails
  | keyof SolutionsOffered
  | keyof HelpFromMicrosoft

export type PartialAzureFormObject = {
  referralMetadata: Partial<ReferralMetadata>
  referralObject: Partial<ReferralAzureFormAPI>
  referralObjectTransformed?: Partial<ReferralAzureFormSections>
  referralSyncs?: SyncRecord[]
}

export const createAzureReferralFormInitialSections = (
  contactRowId = `${Math.floor(Math.random() * 10000)}`
): ReferralAzureFormSections => ({
  customerDetails: {
    customerName: '',
    website: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    firstName: '',
    lastName: '',
    fullName: '',
    email: '',
    phone: undefined,
    addressLine1: '',
    addressLine2: '',
  },
  dealDetails: {
    dealType: undefined,
    dealName: '',
    location: LOCATION_DEFAULT,
    currency: '',
    estimatedValue: 0,
    estimatedCloseDate: '',
    crmId: undefined,
    marketingCampaignID: '',
    solutionPlay: '',
    solutionArea: '',
    partnerRole: '',
    customerPurchaseIntent: undefined,
    teamMembers: [],
    partnerNotes: '',
    contactDetails: [
      {
        id: contactRowId,
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
    ],
  },
  solutionsOffered: {
    companySolutions: [],
    microsoftSolutions: [],
    selectedSolutions: [],
  },
  helpFromMicrosoft: {
    // Fresh create: empty until the user picks a value or CRM/template mapping fills it.
    typeOfHelp: '',
    sellerDealView: undefined,
    notesToMicrosoft: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  },
})

export const azureReferralFormInitialSections =
  createAzureReferralFormInitialSections()

//TBD - update this to match the actual API object
export const mapKeysAzureApiToForm: Record<
  keyof ReferralAzureFormAPI,
  keyof ReferralAzureForm
> = {
  Labra__Id__c: 'id',
  Labra__Website__c: 'website',
  Labra__Solutions_Offered__c: 'solutionsOffered',
  Labra__Customer_Name__c: 'customerName',
  Labra__Address__c: 'addressLine1',
  Labra__Address_Line_2__c: 'addressLine2',
  Labra__State__c: 'state',
  Labra__Postal_Code__c: 'postalCode',
  Labra__Phone__c: 'phone',
  Labra__Deal_Type__c: 'dealType',
  Labra__Solution_Area__c: 'solutionArea',
  Labra__First_Name__c: 'firstName',
  Labra__Last_Name__c: 'lastName',
  Labra__Email__c: 'email',
  Labra__Partner_Notes__c: 'partnerNotes',
  Labra__Country__c: 'country',
  Labra__Location__c: 'location',
  Labra__City__c: 'city',
  Labra__Marketing_Campaign_ID__c: 'marketingCampaignID',
  Labra__Deal_Name__c: 'dealName',
  Labra__Solution_Play__c: 'solutionPlay',
  Labra__Customer_Purchase_Intent__c: 'customerPurchaseIntent',
  Labra__Estimated_Value__c: 'estimatedValue',
  Labra__Estimated_Close_Date__c: 'estimatedCloseDate',
  Labra__CRM_Id__c: 'crmId',
  Labra__Currency__c: 'currency',
  Labra__Type_of_Help__c: 'typeOfHelp',
  Labra__Team_Members__c: 'teamMembers',
  Labra__Notes_to_Microsoft__c: 'notesToMicrosoft',
  Labra__Would_Like_Microsoft_Sellers_To_View_Deal__c:
    'wouldLikeMicrosoftSellersToViewDeal',
  Labra__Full_Name__c: 'fullName',
}

export const mergeAzureReferralFormSections = (
  base: ReferralAzureFormSections,
  patch: Partial<ReferralAzureFormSections>
): ReferralAzureFormSections => ({
  ...base,
  ...patch,
  customerDetails: { ...base.customerDetails, ...patch.customerDetails },
  dealDetails: { ...base.dealDetails, ...patch.dealDetails },
  solutionsOffered: { ...base.solutionsOffered, ...patch.solutionsOffered },
  helpFromMicrosoft: {
    ...base.helpFromMicrosoft,
    ...patch.helpFromMicrosoft,
  },
})
